import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import type * as clusterAssignedRecords from '../../cluster-assigned-records'
import type { Collection } from '../collection'
import * as plugins from '../plugins'
import type { TypeParams } from '../types'

/**
 * When record creation begins (the assigned collection's `isCreating`
 * transitions to true), resets the dimension-filter selections, clears the
 * applied filters, and releases any stale record selection so each session
 * starts from a clean, empty state.
 *
 * `isCreating()` is watched instead of `editingPlugin.edit` because the
 * associations mechanism re-dispatches `edit` on every selection toggle (to
 * sync the selected record ids), which would otherwise reset the filters on
 * each tick.
 */
export const resetFiltersOnModalOpen = store.effects.create(
  (_: ec.Collection<TypeParams>['feature'], collection: Collection) =>
    rxjs
      .combineLatest([
        collection.plugins
          .lookup(plugins.spaceDimensionFilter.Plugin)
          .getWhenReady$(),
        ec.models.CollectionProducer.getEc$<clusterAssignedRecords.TypeParams>(
          ec.Name.Predefined.SpaceClusterAssignedRecord,
          { waitUntilReady: true },
        ),
      ])
      .pipe(
        rxjs.switchMap(([dimensionFilterPlugin, assignedRecordsEc]) =>
          assignedRecordsEc.plugins.currentEntityPlugin.feature.selectors
            .isCreating()
            .asObservable.pipe(
              rxjs.distinctUntilChanged(),
              rxjs.filter(Boolean),
              rxjs.mergeMap(() => [
                dimensionFilterPlugin.actions.resetSelections
                  .from('resetFiltersOnModalOpen')
                  .create(),
                collection.plugins.currentListPlugin.appliedParams.actions.writeAllProperties
                  .from('resetFiltersOnModalOpen')
                  .create({ values: { filters: {}, skip: 0 } }),
                collection.plugins.selectionPlugin.applied.actions.putInSelectionLookup
                  .from('resetFiltersOnModalOpen')
                  .create({
                    key: assignedRecordsEc.associations?.selectionTarget,
                    value: { release: true },
                  }),
              ]),
            ),
        ),
      ),
)
